'use client'

import { useState } from 'react'

import { EyeClosed, Eye } from 'lucide-react'
import { useAppSearchParams } from '@/shared/lib/router'
import { signIn } from 'next-auth/react'
import { FormProvider, useForm } from 'react-hook-form'
import Swal from 'sweetalert2'
import { z } from 'zod'

import { zodResolver } from '@hookform/resolvers/zod'


import { inputClassName, buttonClassName } from '@/features/auth/constants/styles'
import { useAuthContext } from '@/features/auth/contexts/AuthContext'
import { FormItem } from '@/shared/form/FormItem'
import { Input } from '@/shared/form/Input'
import { Button } from '@/shared/ui/Button'

const validationSchema = z
  .object({
    name: z
      .string({ required_error: 'Nome é obrigatório' })
      .min(1, 'Nome é obrigatório'),
    password: z
      .string({ required_error: 'Senha é obrigatória' })
      .min(8, 'Digite no mínimo 8 caracteres')
      .max(128, 'Digite no máximo 128 caracteres')
      .regex(/^\S*$/, 'Senha não pode conter espaços'),
    password_confirmation: z
      .string({ required_error: 'Confirmação de senha é obrigatória' })
      .min(8, 'Digite no mínimo 8 caracteres')
      .max(128, 'Digite no máximo 128 caracteres')
      .regex(/^\S*$/, 'Senha não pode conter espaços')
  })
  .refine(data => data.password === data.password_confirmation, {
    message: 'As senhas não coincidem',
    path: ['password_confirmation']
  })

type AcceptInviteFormData = z.infer<typeof validationSchema>

export function AcceptInviteForm() {
  const searchParams = useAppSearchParams()
  const token = searchParams.get('token')
  const email = searchParams.get('email')
  const { httpClient, callbackUrl, isLoading, setIsLoading } = useAuthContext()
  const methods = useForm<AcceptInviteFormData>({
    resolver: zodResolver(validationSchema)
  })
  const [seePassword, setSeePassword] = useState(false)
  const [seePasswordConfirmation, setSeePasswordConfirmation] = useState(false)

  const onSubmit = async (data: AcceptInviteFormData) => {
    if (!token || !email || isLoading) return
    setIsLoading(true)

    try {
      const { name, password, password_confirmation } = data

      await httpClient.post('/auth/accept-invite', {
        token,
        name,
        password,
        password_confirmation
      })

      await Swal.fire({
        icon: 'success',
        title: 'Conta ativada com sucesso',
        text: 'Clique no botão abaixo e você será redirecionado ao painel de gestão',
        confirmButtonText: 'Entrar'
      })

      const { ok, error, status } = await signIn('domain-sign-in', {
        login: email,
        password,
        redirect: false
      })

      if (ok) {
        window.location.replace(callbackUrl || '/')
        return
      }

      if (error) {
        await Swal.fire({
          icon: 'error',
          title: 'Ops...',
          text: `Ocorreu um erro inesperado, por favor tente novamente mais tarde (${status})`
        })
        window.location.replace('/sign-in')
      }
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error?.message || 'Ocorreu um erro inesperado'
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (!token || !email) return null

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="grid gap-4">
        <FormItem name="name" vertical>
          <FormItem.Label>Nome completo</FormItem.Label>
          <FormItem.Control>
            <Input name="name" autoComplete="name" className={inputClassName} />
          </FormItem.Control>
        </FormItem>

        <FormItem name="password" vertical>
          <FormItem.Label>Senha</FormItem.Label>
          <div className="relative">
            <FormItem.Control>
              <Input
                name="password"
                type={seePassword ? 'text' : 'password'}
                autoComplete="new-password"
                className={inputClassName}
              />
            </FormItem.Control>
            <button
              type="button"
              className="absolute right-0 top-1/2 flex -translate-y-1/2 items-center gap-x-1 pr-3"
              onClick={() => setSeePassword(!seePassword)}
            >
              {seePassword ? (
                <Eye className="h-4 w-4 text-gray-500" />
              ) : (
                <EyeClosed className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
        </FormItem>

        <FormItem name="password_confirmation" vertical>
          <FormItem.Label>Confirmar senha</FormItem.Label>
          <div className="relative">
            <FormItem.Control>
              <Input
                name="password_confirmation"
                type={seePasswordConfirmation ? 'text' : 'password'}
                autoComplete="new-password"
                className={inputClassName}
              />
            </FormItem.Control>
            <button
              type="button"
              className="absolute right-0 top-1/2 flex -translate-y-1/2 items-center gap-x-1 pr-3"
              onClick={() => setSeePasswordConfirmation(!seePasswordConfirmation)}
            >
              {seePasswordConfirmation ? (
                <Eye className="h-4 w-4 text-gray-500" />
              ) : (
                <EyeClosed className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
        </FormItem>

        <Button
          type="submit"
          variant="primary"
          disabled={isLoading}
          loading={isLoading}
          className={buttonClassName}
        >
          {isLoading ? 'Criando conta...' : 'Criar conta'}
        </Button>
      </form>
    </FormProvider>
  )
}
